import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function VeilifyPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Veilify Photo Vault"
      date="NOVEMBER 2025"
    >
      <p>
        Developer: Aleksandra Rakitina
        <br />
        Contact (privacy requests): alexwadek@gmail.com
      </p>

      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “Veilify Photo Vault” (the “App”) processes
        information to provide a private, local-only vault for photos and videos on your
        device. The App’s core promise is to store media exclusively on-device, encrypted,
        with access controlled by a PIN and, if you choose, Face ID/Touch ID. We do not
        require a cloud account to use the vault, and we do not back up your hidden media to
        our servers. This Policy covers onboarding, PIN/biometric setup, gallery import and
        organization into albums, on-device encryption and decryption, subscription
        management, analytics focused on reliability, and customer support. By using the App,
        you acknowledge this Policy and agree to the practices described. If you do not
        agree, uninstall the App and contact us regarding your data options.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The data controller responsible for decisions about how and why your data is
        processed is Aleksandra Rakitina. Questions, privacy requests, and security notices
        may be sent to alexwadek@gmail.com. We respond within a reasonable time and may
        request limited additional details to verify your identity before acting, such as
        confirmation from the email associated with your purchase or from within the App.
        Communications about privacy may be retained for compliance and recordkeeping where
        allowed by law. If your request concerns platform-level operations like subscription
        cancellation or refund, we will guide you to the relevant system settings while
        continuing to handle privacy-specific matters within our remit.
      </p>

      <div className={s.title}>3) Key Principles (Local-First, Encryption, Minimization)</div>
      <p>
        The App is designed around three principles. First, local-first: photos and videos
        you import into the vault remain on your device within an app-scoped, encrypted
        storage area and are not uploaded to our infrastructure. Second, encryption: media
        files are encrypted using AES; encryption keys are stored using platform mechanisms
        (Keychain on iOS, Keystore on Android) and accessed through secure APIs. Third,
        minimization: we collect only what is necessary for core features like
        authentication, entitlements, stability analytics, and support. We avoid processing
        the content of your private media for analytics or marketing. Where feasible, we
        store operational metadata locally and keep remote event data aggregate and
        non-content-bearing.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        For clarity, “Personal Data” means any information relating to an identified or
        identifiable person, including contact data or subscription identifiers. “Vault
        Media” refers to photos and videos you choose to import into the App’s encrypted
        storage. “Local Metadata” means on-device records such as filenames, album
        membership, timestamps, and internal identifiers. “Processing” includes collection,
        storage, encryption, decryption, use, and deletion. “Biometric Data” as used here
        refers to device-level biometric unlock signals (Face ID, Touch ID) that the
        platform verifies; the App receives only a yes/no success from the device’s secure
        enclave and does not store your biometric template. “Service Providers” are third
        parties acting on our instructions to deliver subscriptions, analytics, or crash
        diagnostics.
      </p>

      <div className={s.title}>5) What We Do Not Collect</div>
      <p>
        We do not collect, upload, or remotely store the content of your Vault Media. We do
        not operate a cloud service for the vault and do not transmit your hidden photos or
        videos to our servers. We do not scan media for marketing or advertising, do not use
        ad SDKs to profile you based on vault content, and do not sell Personal Data. We do
        not access your address book, precise location, or microphone. If you enable
        optional diagnostics via the platform, we still avoid attaching vault file contents
        to logs. Our analytics events focus on reliability (for example, whether import
        succeeded) rather than the subject matter of your photos or videos.
      </p>

      <div className={s.title}>6) Categories of Data We Process</div>
      <p>
        Depending on your choices, we may process: (a) Local Metadata stored on-device to run
        the vault (album names, internal IDs, timestamps, basic flags like “favorite”); (b)
        subscription and entitlement metadata from RevenueCat (product IDs, trial status,
        renewal outcomes, expiration/cancellation dates) to grant premium features like
        unlimited items and biometric unlock; (c) minimal analytics and crash diagnostics
        (app version, OS version, device model, anonymized error codes, feature counts) to
        improve stability; (d) support correspondence you send to our contact address. None
        of these categories require transmitting or analyzing the content of your Vault
        Media. Where possible, diagnostics are aggregated or pseudonymized and retained only
        as long as necessary.
      </p>

      <div className={s.title}>7) Device Permissions and Photo Import Behavior</div>
      <p>
        To import photos or videos into the vault, the App requests permission to read from
        your device photo library. We access only the items you explicitly select. Depending
        on the platform and your settings, importing may copy items into the encrypted vault
        while leaving the originals in the system Photos app. In that case, you can choose to
        delete the originals from the system Photos app after confirming they are safely
        stored in the vault. The App does not silently delete originals. Deletions you
        perform in the vault remove the encrypted copies; they do not affect files stored
        elsewhere unless you explicitly choose to remove them there as well.
      </p>

      <div className={s.title}>8) PIN Code, Biometric Unlock, and Key Handling</div>
      <p>
        Access to the vault requires a PIN you create during onboarding. You may enable Face
        ID/Touch ID via the platform’s biometric APIs. Biometric verification is performed by
        the device; the App receives only a success/failure signal and never stores your
        biometric template. Encryption keys are generated and stored with platform-secured
        facilities (Keychain/Keystore) and are not exported by the App. For additional
        safety, the vault may require the PIN again after periods of inactivity, app
        relaunch, or settings changes. If you forget your PIN and have not enabled a fallback
        such as device biometrics, we cannot recover it, and you may lose access to encrypted
        content, as we do not maintain a master recovery key.
      </p>

      <div className={s.title}>9) Local Storage, Encryption, and Deletion</div>
      <p>
        Vault Media are stored in an app-scoped directory encrypted with AES. Local Metadata
        reside in SQLite/Hive databases within the app sandbox. When you delete an item from
        the vault, the App removes the encrypted file and its metadata from the sandbox. Due
        to how modern filesystems manage storage, physical overwriting may not occur
        immediately; however, the data is no longer accessible through the App. Uninstalling
        the App removes the sandbox and the encryption keys held by the app context, which
        permanently prevents decryption of any remaining vault files. Always export items you
        wish to keep before uninstalling or factory-resetting your device.
      </p>

      <div className={s.title}>
        10) Subscriptions, Billing, Trials, and Entitlements
      </div>
      <p>
        The App offers subscriptions managed through platform billing via RevenueCat. Current
        plans include a 3-day trial and pricing such as $3.99 per week or $29.99 per year.
        The free tier may limit vault capacity (for example, up to 50 photos/videos and one
        album). Paid features can include unlimited capacity, multiple albums, and biometric
        unlock. We receive entitlement status, product identifiers, trial eligibility,
        renewal outcomes, expiration dates, and cancellation status to grant or revoke
        access. We do not receive or store payment card numbers. Manage or cancel
        subscriptions in your device account settings. Refunds, grace periods, and price
        change notices follow platform rules and applicable law.
      </p>

      <div className={s.title}>
        11) Analytics, Performance, and Crash Diagnostics
      </div>
      <p>
        We use analytics to understand reliability and performance, not to inspect your Vault
        Media. Typical events may include onboarding completion, vault unlock success/failure
        counts, import/export attempts, album creation counts, paywall impressions, trial
        starts, purchases, renewals, cancellations, and error codes. Crash diagnostics help
        identify device- or OS-specific issues. Where possible, analytics are aggregated or
        pseudonymized and exclude file contents or thumbnails. You can reduce analytics via
        platform privacy settings where available, understanding it may limit our ability to
        troubleshoot rare crashes. We do not combine analytics with ad networks for targeted
        advertising.
      </p>

      <div className={s.title}>12) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect Personal Data
        from users under the age where parental consent is required by applicable law. If you
        believe a minor has used the App without appropriate authorization, contact
        alexwadek@gmail.com and we will remove information and terminate access where
        applicable. Parents and guardians can use device-level restrictions to limit photo
        access, purchases, and biometric enrollment. If we learn that we inadvertently
        processed data from a child without proper consent, we will promptly delete such data
        consistent with legal requirements.
      </p>

      <div className={s.title}>13) Your Rights and Choices</div>
      <p>
        Subject to local law, you may have rights to access, correct, update, or delete
        Personal Data; restrict or object to certain processing; or request data portability.
        Because the vault stores media locally and does not upload Vault Media to our
        servers, many controls are available directly in the App: you can add or remove
        items, rename or delete albums, change the PIN, enable/disable biometrics, and
        export items back to the system Photos app. For requests relating to subscription
        records or support correspondence, email alexwadek@gmail.com from the address
        associated with your purchase. We will verify your identity before responding and
        explain any legal or technical limits.
      </p>

      <div className={s.title}>14) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational safeguards designed to
        protect data against unauthorized access, alteration, disclosure, or loss. These
        include encryption in transit where applicable, encryption at rest for Vault Media,
        secure key storage using Keychain/Keystore, least-privilege access to operational
        tools, credential rotation, and environment separation for development and
        production. We avoid using production Personal Data for testing and review
        dependencies for known vulnerabilities. While no system can be guaranteed perfectly
        secure, our local-first architecture reduces exposure by keeping private media off
        the network and behind device protections and your vault PIN.
      </p>

      <div className={s.title}>15) Data Retention and Deletion</div>
      <p>
        We retain Personal Data only as long as necessary for the purposes described here,
        including providing the service, maintaining security, complying with legal
        obligations, and resolving disputes. Vault Media and Local Metadata persist on your
        device until you delete them or uninstall the App. Subscription and entitlement
        metadata from RevenueCat may be retained for accounting and fraud prevention in
        pseudonymized or aggregated form. Crash and performance logs are kept for a limited
        trend window before deletion or de-identification. Verified deletion requests for
        server-side records we control (such as support emails) are handled within a
        reasonable time.
      </p>

      <div className={s.title}>
        16) Import, Export, and External Destinations
      </div>
      <p>
        When you import items, the App creates encrypted copies inside the vault. Depending
        on your preference, you may manually delete the originals from the system Photos app
        to avoid duplicates. When you export or share items from the vault, you decrypt and
        copy them to destinations you choose (for example, Photos, Files, cloud drives, or
        messaging apps). Those destinations have their own privacy practices that we do not
        control. Deleting an item in the vault does not remove exported copies elsewhere.
        Review the settings and terms of any external service before sharing sensitive media.
      </p>

      <div className={s.title}>17) International Transfers</div>
      <p>
        Because the App operates primarily on-device without cloud storage of Vault Media,
        international transfers of vault content do not occur. Limited Personal Data may be
        processed by service providers in other jurisdictions for subscriptions, analytics,
        or crash diagnostics. Where required, we rely on appropriate safeguards for
        cross-border transfers, such as contractual protections and platform mechanisms.
        Regardless of location, providers must implement security measures consistent with
        this Policy and applicable law. You may contact us for information about relevant
        transfer mechanisms and, where appropriate, summaries of key contractual commitments
        that do not reveal confidential commercial information.
      </p>

      <div className={s.title}>
        18) Legal Bases (EEA/UK) and Global Principles
      </div>
      <p>
        For users in the EEA/UK, our legal bases include performance of a contract (core
        vault functionality and subscriptions), legitimate interests (security, fraud
        prevention, stability improvements) balanced against your rights through
        minimization and user controls, consent (optional analytics categories or
        communications where required), and legal obligations (records and compliance).
        Globally, we apply similar principles and safeguards regardless of location, adapting
        to local requirements as applicable. If a purpose materially changes, we will update
        this Policy and, where required, seek additional consent.
      </p>

      <div className={s.title}>
        19) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        As a native app, we do not use browser cookies. SDKs may store identifiers needed for
        subscriptions, analytics, and crash reporting. These identifiers function similarly
        to cookies by enabling entitlement checks and measuring reliability. The App does not
        rely on advertising identifiers for targeted ads. Browser “Do Not Track” signals
        generally do not apply to native apps; nonetheless, we keep SDK usage scoped to
        necessary purposes and avoid combining identifiers across services for unrelated
        marketing. Platform settings may allow you to reset device identifiers or limit
        certain measurements.
      </p>

      <div className={s.title}>
        20) Free Tier Limits, Abuse Prevention, and Fair Use
      </div>
      <p>
        The free tier may cap the number of items (for example, up to 50 photos/videos) and
        restrict albums. These limits are enforced locally using counters and entitlement
        checks. Attempts to bypass limits or automate imports in ways that degrade service
        quality may trigger protective measures, such as temporary lockouts or
        re-authentication prompts. Paid plans remove these caps and enable premium features
        like biometric unlock, subject to fair-use principles intended to preserve app
        responsiveness and device storage health. We will communicate material changes to
        limits according to platform rules and applicable law.
      </p>

      <div className={s.title}>21) Biometric Information Clarification</div>
      <p>
        When you enable Face ID/Touch ID, biometric matching occurs within secure hardware
        components controlled by the operating system. The App does not receive your facial
        map or fingerprint; it only receives a binary result that allows unlocking the vault
        if successful. You can disable biometrics at any time and return to PIN-only access.
        If biometrics fail repeatedly, the device may require the device passcode before
        allowing further attempts, at which point the App will also require your vault PIN.
        These behaviors follow platform policies designed to prevent brute-force attacks and
        protect sensitive content.
      </p>

      <div className={s.title}>
        22) Transparency for Reviews and Platform Compliance
      </div>
      <p>
        To ensure clarity for reviewers and users, we disclose that vault content is stored
        only on-device, encrypted, and not uploaded to our servers. We also disclose that
        deleting the App removes access to encrypted content and associated keys, which can
        render remaining vault files permanently unreadable. Any future features that could
        change data flows (for example, optional cloud backup) will be disabled by default
        and will include explicit in-app disclosures and updated Policy text before
        activation. We build features to comply with platform guidelines, including biometric
        prompts provided by the operating system.
      </p>

      <div className={s.title}>23) Data Accuracy and User Controls</div>
      <p>
        We rely on accurate information for subscription management and support interactions.
        You can manage most privacy-sensitive operations directly in the App: change the PIN,
        toggle biometrics, create or delete albums, import, export, and permanently delete
        items. You can also reduce analytics where platform settings allow. If you notice
        inaccuracies in subscription status or entitlement display, contact us and we will
        coordinate with the provider to correct records. Some changes may take time to
        propagate across caches or after restoring purchases on a new device.
      </p>

      <div className={s.title}>24) Security Incident Response</div>
      <p>
        If we become aware of a security incident affecting Personal Data we control (for
        example, subscription or support records), we will investigate promptly, take
        appropriate remediation steps, and notify affected users and authorities where
        required by law. Because the App stores Vault Media locally and does not upload its
        contents to our servers, the scope of any remote incident is limited to non-content
        data. You can enhance your protection by using a strong device passcode, enabling
        automatic OS updates, and keeping the App up to date to receive security fixes and
        improvements.
      </p>

      <div className={s.title}>
        25) Third-Party Links and External Content
      </div>
      <p>
        The App may present optional links to documentation, support pages, or platform
        settings. When you follow a link, you leave the App environment and the destination’s
        privacy practices apply. We do not control third-party content and are not
        responsible for their policies or security. Review any requests for permissions or
        personal information carefully before proceeding. If a linked resource changes terms
        or becomes unavailable, we may update or remove the link in a future release without
        prior notice to maintain safety and consistency with this Policy.
      </p>

      <div className={s.title}>26) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory developments.
        The “Last Updated” date will change when updates occur. Material changes that affect
        your rights or our processing will be highlighted in-app, and when required, we will
        request your consent for new uses. Continued use of the App after changes take effect
        signifies acceptance. We encourage you to review this Policy periodically, especially
        before enabling new features or changing security settings such as PIN length or
        biometric unlock.
      </p>

      <div className={s.title}>
        27) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights or raise privacy questions, email alexwadek@gmail.com from
        the address associated with your purchase or app account and specify the request type
        (access, deletion, correction, restriction, objection, portability). We will verify
        your identity to prevent unauthorized access or deletion and may request limited
        additional information, such as a transaction reference or a confirmation code sent
        to your device. Where feasible, we will provide responses in a portable and
        machine-readable format. We may decline or limit requests when permitted by law, for
        example if fulfilling a request would compromise the security or privacy of others.
      </p>
    </PolicyLayout>
  );
}
